import type { SelectChangeEvent } from '@mui/material';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material';
import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import useUniqueId from '@hooks/useUniqueId';

interface TimeSelectorProps {
  /**
   * Callback function to handle value changes.
   * Takes a number as a parameter.
   * @function
   * @name onChange
   * @param {number} value - The selected time value. Represents a number of hours in the future.
   */
  onChange: (value: number) => void;
}

const messages = defineMessages({
  hour: {
    id: 'timeSelector.hour',
    defaultMessage: '1 hour',
  },
  day: {
    id: 'timeSelector.day',
    defaultMessage: '1 day',
  },
  week: {
    id: 'timeSelector.week',
    defaultMessage: '7 days',
  },
  label: {
    id: 'timeSelector.label',
    defaultMessage: 'Link Expires After',
  },
  helperText: {
    id: 'timeSelector.helperText',
    defaultMessage: 'The share link will expire after this amount of time.',
  },
});

/**
 * TimeSelector component.
 * Renders a select input for selecting a time value.
 * Calls the onChange function with the selected time value.
 * @component
 * @param {TimeSelectorProps} props - Component props
 * @returns {JSX.Element} - Rendered TimeSelector component
 */
const TimeSelector: React.FC<TimeSelectorProps> = ({
  onChange,
}): JSX.Element => {
  const { formatMessage } = useIntl();
  /**
   * State to keep track of the selected time value.
   */
  const [time, setTime] = useState<number>(1);
  const id = useUniqueId('time-selector');
  const labelId = useUniqueId('time-selector-label');

  /**
   * Callback function for handling select input value changes.
   * @param {SelectChangeEvent<number>} event - The event object from the select input.
   */
  const handleChange = (event: SelectChangeEvent<number>): void => {
    const newTime = Number(event.target.value);
    setTime(newTime);
    onChange(newTime);
  };

  return (
    <FormControl fullWidth required>
      {/* Render the select input label */}
      <InputLabel id={labelId}>{formatMessage(messages.label)}</InputLabel>
      {/* Render the select input */}
      <Select
        labelId={labelId}
        id={id}
        value={time}
        label={formatMessage(messages.label)}
        onChange={handleChange}
      >
        {/* Render options for the select input */}
        <MenuItem value={1}>{formatMessage(messages.hour)}</MenuItem>
        <MenuItem value={24}>{formatMessage(messages.day)}</MenuItem>
        <MenuItem value={168}>{formatMessage(messages.week)}</MenuItem>
      </Select>
      <FormHelperText>{formatMessage(messages.helperText)}</FormHelperText>
    </FormControl>
  );
};

export default TimeSelector;
